import { useTheme } from '@mui/material';
import DSBox from '../../design-system/DSBox';
import {
  PENDULUM_BALL_DIAMETER,
  PENDULUM_DIAMETER,
  PENDULUM_ROD_HEIGHT,
  PENDULUM_ROD_WIDTH,
} from '../../consts/clockDimensions';
import { PendulumLogoType } from '../../consts/pendulumLogo';
import TintIcon from './TintIcon';
import { resolveLogoDiameterPx } from './resolveLogoDiameterPx';

const Pendulum = ({
  pendulumLogo,
  pendulumLogoType,
  logoDiameterPx,
  pendulumLabel,
  backgroundColor,
  pendulumColor,
  rodColor,
  isStatic,
  scale = 1,
}: {
  pendulumLogo?: string;
  // 'full' is the artist's own full-color image filling the whole bob - it
  // must render in its own colors, not tinted like a 'sized'/legacy icon
  // logo (see the plain <img> branch below). Absent/undefined means 'sized'.
  pendulumLogoType?: PendulumLogoType;
  // Resolved device-px diameter to draw the logo at - see
  // resolveLogoDiameterPx, which owns the draft/approved and
  // sized/legacy-default rules. Falls back to the legacy-default sizing
  // (as if no collection/size context exists at all) when omitted, so
  // callers with no notion of a collection keep their current look.
  logoDiameterPx?: number;
  pendulumLabel?: string;
  backgroundColor: string;
  pendulumColor: string;
  rodColor: string;
  isStatic?: boolean;
  scale?: number;
}) => {
  const { zIndex } = useTheme();
  const pendulumDiameter = `${PENDULUM_DIAMETER * scale}px`;
  const ballDiameterPx = PENDULUM_BALL_DIAMETER * scale;
  const ballDiameter = `${ballDiameterPx}px`;
  const pendulumRodWidth = `${PENDULUM_ROD_WIDTH * scale}px`;
  const pendulumRodHeight = `${PENDULUM_ROD_HEIGHT * scale}px`;
  const resolvedLogoDiameterPx =
    logoDiameterPx ?? resolveLogoDiameterPx({ scale });

  return (
    <DSBox
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
      }}
    >
      <DSBox
        sx={{
          position: 'absolute',
          top: '68%',
          borderRadius: '50%',
          backgroundColor: backgroundColor,
          overflow: 'hidden',
          width: pendulumDiameter,
          height: pendulumDiameter,
        }}
      >
        <DSBox
          sx={{
            position: 'absolute',
            marginTop: `${-400 * scale}px`,
            marginLeft: `${133 * scale}px`,
            transformOrigin: '50% 0px',
            animation: isStatic
              ? 'none'
              : 'pendulum-swing 2s ease-in-out infinite',
          }}
        >
          <DSBox
            sx={{
              backgroundColor: rodColor,
              width: pendulumRodWidth,
              height: pendulumRodHeight,
              position: 'absolute',
              transform: 'translateX(-50%)',
              zIndex: zIndex.pendulumRod,
              boxShadow: `
              0px 20px 30px rgba(0, 0, 0, 0.3),
              0px 0px 15px rgba(0, 0, 0, 0.15)
            `,
            }}
          />
          <DSBox
            sx={{
              backgroundColor: pendulumColor,
              width: ballDiameter,
              height: ballDiameter,
              position: 'absolute',
              top: pendulumRodHeight,
              transform: 'translate(-50%, -50%)',
              zIndex: zIndex.pendulumBall,
              borderRadius: '50%',
              boxShadow: `
              0px 20px 30px rgba(0, 0, 0, 0.3),
              0px 0px 15px rgba(0, 0, 0, 0.15)
            `,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {(pendulumLogo || pendulumLabel) && (
              // A resolved size can be as large as the ball itself, so clip
              // to the ball's own circle (absolutely overlaid) - otherwise a
              // near-max-size square logo's corners paint past the round
              // ball edge, same issue as the clock app's Pendulum.kt. Harmless
              // for a smaller logo, which never reaches the clip edge anyway.
              // The label shares this column so it stacks under the logo -
              // as a sibling of the overlay it centered on top of it instead.
              <DSBox
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {pendulumLogo && (
                  <DSBox
                    sx={{
                      // The 4% nudge is a legacy-icon convention (a sized logo
                      // sits visually a touch above center to match the old
                      // fixed-size look). A 'full' image already fills 100% of
                      // the ball and the device draws it centered - keeping the
                      // margin here leaves a sliver of bob color at the bottom
                      // that the real device doesn't show.
                      mb:
                        pendulumLogoType === 'full'
                          ? 0
                          : `${PENDULUM_BALL_DIAMETER * scale * 0.04}px`,
                    }}
                  >
                    {pendulumLogoType === 'full' ? (
                      <img
                        src={pendulumLogo}
                        alt=""
                        width={resolvedLogoDiameterPx}
                        height={resolvedLogoDiameterPx}
                        style={{ display: 'block', objectFit: 'cover' }}
                      />
                    ) : (
                      <TintIcon
                        src={pendulumLogo}
                        color={backgroundColor}
                        width={resolvedLogoDiameterPx}
                        height={resolvedLogoDiameterPx}
                      />
                    )}
                  </DSBox>
                )}
                {pendulumLabel && (
                  <DSBox
                    sx={{
                      color: backgroundColor,
                      fontFamily: 'Gilroy-SemiBold',
                      fontSize: `${Math.max(8, Math.round(PENDULUM_BALL_DIAMETER * scale * 0.1))}px`,
                      textAlign: 'center',
                      mt: `${-PENDULUM_BALL_DIAMETER * scale * 0.03}px`,
                      maxWidth: `${PENDULUM_BALL_DIAMETER * scale * 0.8}px`,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.2,
                      backgroundColor: 'transparent',
                      flexShrink: 0,
                    }}
                  >
                    {pendulumLabel}
                  </DSBox>
                )}
              </DSBox>
            )}
          </DSBox>
        </DSBox>
      </DSBox>
      <style>
        {`
          @keyframes pendulum-swing {
            0% {
              transform: rotate(-4deg); /* Swing left */
            }
            50% {
              transform: rotate(4deg); /* Swing right */
            }
            100% {
              transform: rotate(-4deg); /* Back to left */
            }
          }
        `}
      </style>
    </DSBox>
  );
};

export default Pendulum;
